/**
 * ffmpegClient
 * ---------------------------------------------------------------
 * Lazy-loaded singleton around @ffmpeg/ffmpeg (WASM). Loads the
 * single-threaded core from a CDN (no COOP/COEP headers required)
 * on first call and reuses the same instance for the rest of the
 * page's lifetime. Callers pay ~11 MB (gzipped) once, then trims
 * are effectively free.
 *
 * Public API: `getFFmpeg()` returns a promise that resolves to a
 * ready-to-use FFmpeg instance. Never awaited at module scope —
 * the network request only happens when the user actually asks
 * to trim.
 */

let ffmpegPromise = null;

// Pinned versions so a bad upstream publish can't break trims.
const CORE_VERSION = '0.12.10';
const CORE_BASE = `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/umd`;

/**
 * Returns a loaded, ready FFmpeg instance. Safe to call many times —
 * only one load ever happens. Throws if the network fetch fails so
 * the caller can fall back to another trim path.
 */
export const getFFmpeg = () => {
    if (ffmpegPromise) return ffmpegPromise;

    ffmpegPromise = (async () => {
        const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
            import('@ffmpeg/ffmpeg'),
            import('@ffmpeg/util'),
        ]);

        const ffmpeg = new FFmpeg();
        // Fetch core JS + WASM through blob URLs so they satisfy the
        // worker's same-origin loader without needing app-side CORS
        // config.
        const [coreURL, wasmURL] = await Promise.all([
            toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
            toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
        ]);
        await ffmpeg.load({ coreURL, wasmURL });
        return ffmpeg;
    })();

    // If the load fails, clear the cache so a retry can try again.
    ffmpegPromise.catch(() => { ffmpegPromise = null; });
    return ffmpegPromise;
};

/**
 * Trim [startSec, endSec] out of `file` and return a new File.
 * Uses `-c copy` first for a near-instant, quality-preserving cut;
 * falls back to a fast x264 re-encode when copy produces an invalid
 * output (e.g. no keyframe near the start).
 */
export const ffmpegTrim = async ({ file, startSec, endSec, onProgress }) => {
    const ffmpeg = await getFFmpeg();
    const { fetchFile } = await import('@ffmpeg/util');

    const inputExt = (file.name?.split('.').pop() || 'mp4').toLowerCase();
    const inputName = `input.${inputExt}`;
    const outputName = 'output.mp4';

    const progressHandler = onProgress
        ? ({ progress }) => onProgress(Math.max(0, Math.min(1, progress)))
        : null;
    if (progressHandler) ffmpeg.on('progress', progressHandler);

    try {
        await ffmpeg.writeFile(inputName, await fetchFile(file));

        const dur = Math.max(0.1, endSec - startSec).toFixed(3);
        const startArg = Math.max(0, startSec).toFixed(3);

        // Attempt 1: fast stream copy (no re-encode). Extremely fast and
        // preserves quality byte-for-byte. Uses `-ss` before `-i` for a
        // keyframe seek. If the source has sparse keyframes the start may
        // snap up to ~2s earlier — acceptable for social clips.
        const copyArgs = [
            '-ss', startArg,
            '-i', inputName,
            '-t', dur,
            '-c', 'copy',
            '-movflags', '+faststart',
            '-y',
            outputName,
        ];

        let ok = false;
        try {
            await ffmpeg.exec(copyArgs);
            const data = await ffmpeg.readFile(outputName);
            if (data && data.byteLength > 1024) {
                const baseName = (file.name || 'vibe').replace(/\.[^.]+$/, '');
                const outFile = new File([data], `${baseName}-trim.mp4`, { type: 'video/mp4' });
                await ffmpeg.deleteFile(inputName).catch(() => {});
                await ffmpeg.deleteFile(outputName).catch(() => {});
                ok = true;
                return outFile;
            }
        } catch {
            // fall through to re-encode
        }

        if (!ok) {
            // Attempt 2: fast x264 re-encode for exact-frame trim. Ultrafast
            // preset keeps this ~3-5× faster than realtime for 1080p on
            // typical laptops.
            const encodeArgs = [
                '-ss', startArg,
                '-i', inputName,
                '-t', dur,
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-crf', '23',
                '-c:a', 'aac',
                '-b:a', '128k',
                '-movflags', '+faststart',
                '-y',
                outputName,
            ];
            await ffmpeg.exec(encodeArgs);
            const data = await ffmpeg.readFile(outputName);
            const baseName = (file.name || 'vibe').replace(/\.[^.]+$/, '');
            const outFile = new File([data], `${baseName}-trim.mp4`, { type: 'video/mp4' });
            await ffmpeg.deleteFile(inputName).catch(() => {});
            await ffmpeg.deleteFile(outputName).catch(() => {});
            return outFile;
        }
        // Unreachable — the try above either returns or throws.
        throw new Error('ffmpeg trim produced no output');
    } finally {
        if (progressHandler) {
            try { ffmpeg.off('progress', progressHandler); } catch { /* noop */ }
        }
    }
};
